// Pieces shared by the compare and compat screens (scan v2): the two-side
// selection state, the credit pill and the out-of-credits card.
import React, { useCallback, useState } from 'react';
import type { SideProduct } from './productSideFlow/reducer';
import { AnimatedSwap, FlashHalo } from './screenMotion';

export type PairSide = 'a' | 'b';

type Pending = { side: PairSide; productId?: number };

export interface PairSelection {
  productA: SideProduct | null;
  productB: SideProduct | null;
  /** Remount key per side: bumps on swap so each side restarts from its new initial product. */
  sideKey: number;
  initialA?: SideProduct;
  initialB?: SideProduct;
  onChange: (side: PairSide) => (product: SideProduct | null) => void;
  isPending: (side: PairSide) => boolean;
  markPending: (side: PairSide) => void;
  duplicate: boolean;
  canSubmit: boolean;
  canSwap: boolean;
  swap: () => void;
}

/**
 * Ready products of both sides. A side marked pending (409 ingredients_pending)
 * stays blocked until it holds another product; two equal ids block the pair.
 */
export function usePairSelection(initialA?: SideProduct, initialB?: SideProduct, pendingSide?: PairSide): PairSelection {
  const [initials, setInitials] = useState({ a: initialA, b: initialB });
  const [productA, setProductA] = useState<SideProduct | null>(initialA ?? null);
  const [productB, setProductB] = useState<SideProduct | null>(initialB ?? null);
  const [sideKey, setSideKey] = useState(0);
  const [pending, setPending] = useState<Pending | null>(() => {
    if (!pendingSide) return null;
    const product = pendingSide === 'a' ? initialA : initialB;
    return { side: pendingSide, productId: product?.productId };
  });

  const onChange = useCallback((side: PairSide) => (product: SideProduct | null) => {
    if (side === 'a') setProductA(product);
    else setProductB(product);
    setPending((current) => {
      if (current?.side !== side) return current;
      return product && (current.productId === undefined || product.productId === current.productId) ? current : null;
    });
  }, []);

  const productOf = (side: PairSide) => (side === 'a' ? productA : productB);
  const isPending = (side: PairSide) => {
    const product = productOf(side);
    return pending?.side === side && product !== null && (pending.productId === undefined || pending.productId === product.productId);
  };
  const markPending = (side: PairSide) => { setPending({ side, productId: productOf(side)?.productId }); };

  const duplicate = productA !== null && productB !== null && productA.productId === productB.productId;
  const canSubmit = productA !== null && productB !== null && !duplicate && !isPending('a') && !isPending('b');
  const canSwap = productA !== null && productB !== null;

  const swap = () => {
    if (!canSwap) return;
    setInitials({ a: productB, b: productA });
    setProductA(productB);
    setProductB(productA);
    setPending((current) => (current ? { ...current, side: current.side === 'a' ? 'b' : 'a' } : null));
    setSideKey((k) => k + 1);
  };

  return {
    productA,
    productB,
    sideKey,
    initialA: initials.a,
    initialB: initials.b,
    onChange,
    isPending,
    markPending,
    duplicate,
    canSubmit,
    canSwap,
    swap,
  };
}

/** "{cost} credits · {left} left", or "Need {cost} credits" when short. */
export function CreditPill({ cost, left, outOfQuota }: { cost: number | null; left: number | null; outOfQuota: boolean }) {
  if (cost === null) return null;
  const text = `${cost} credits${left !== null ? ` · ${left} left` : ''}`;
  return (
    <FlashHalo depsKey={left ?? 0}>
      <span
        className={outOfQuota ? 'pill' : 'pill pill--teal'}
        style={outOfQuota ? { color: 'var(--c-error)' } : undefined}
        title={outOfQuota ? 'Not enough credits' : text}
        data-testid="credit-pill"
      >
        {outOfQuota
          ? <><span className="dot dot--coral" /><AnimatedSwap value={`Need ${cost} credits`} /></>
          : <><span className="dot" /><AnimatedSwap value={text} /></>}
      </span>
    </FlashHalo>
  );
}

/** Shown instead of the sides when the action cannot be paid: points to the wallet. */
export function OutOfCreditsCard({ closeLabel, onBuy, onClose }: { closeLabel: string; onBuy: () => void; onClose: () => void }) {
  return (
    <div className="text-center stack-3 items-center">
      <div
        className="w-12 h-12 flex items-center justify-center rounded-full mx-auto"
        style={{ background: 'rgba(196,103,58,0.1)', color: 'var(--c-error)' }}
      >✕</div>
      <p className="t-small" style={{ fontWeight: 600, color: 'var(--ink)' }}>Not enough credits</p>
      <p className="t-small t-mute">Insufficient credits — top up or wait for your monthly reset.</p>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
        <button type="button" onClick={onBuy} className="btn btn--primary btn--sm">Buy credits</button>
        <button type="button" onClick={onClose} className="btn btn--ghost btn--sm">{closeLabel}</button>
      </div>
    </div>
  );
}
